import { report } from '../_test/data/progress';
import { getProgressActivitiesForStudents } from "../../../services/API";
import {locales} from "Constants/constants";
import { generateProgressReport } from '../util/generators/generateProgressReport';
import { accountForTrendline } from '../state/progress/creators';
import {hasPendingAssessment} from '../util/pendingAssessment';
import {fetchARMScore} from '../util/getArmScore';
import { fetchScaledCutlines } from '../../../services/query/benchmarkReportAPI';
import { get } from 'lodash';
import store from '../../../store';
import { ScoreType } from '@/enums/ScoreType';

// Helper function to wait for Redux state condition
const waitForState = (selector, timeout = 5000) => {
  return new Promise((resolve, reject) => {
    const startTime = Date.now();
    
    // Check if condition is already met
    if (selector(store.getState())) {
      return resolve(store.getState());
    }
    
    // Set up subscription
    const unsubscribe = store.subscribe(() => {
      if (selector(store.getState())) {
        unsubscribe();
        resolve(store.getState());
      } else if (Date.now() - startTime > timeout) {
        unsubscribe();
        reject(new Error('Timeout waiting for state condition'));
      }
    });
  });
};

// TODO generate report, rename method, add scale method (or reconsider activity caching)
export const fetchActivities = (studentId, equating, locale, scale) => {
  if(locale === locales.ES_MX) equating = false; // this is the line that causes Spanish to not have equating for AE-14215
  // Fetch activities for a particular student
  const state = store.getState();
  const scoreType = state.userEntitlement.data.scoreType;
  
  const students = [{id: studentId.value}];
  const limit = 600;
  const type = scoreType === ScoreType.ISIP ? ["assessment"] : ["assessment", "tutor"];
  
  return getProgressActivitiesForStudents(students, limit, type, equating, locale, scale).then((activities) => {
    activities.map((act)=>{
      act.student.grade = studentId.grade > 6 ? 6 : studentId.grade;
    });
    return activities;
  });
}

export const fetchReport = async (student, metric, scale, equating, locale, additionalProps={}) => {
  try {
    const progressPromise = fetchProgressActivities(student, equating, locale, metric, scale, additionalProps);
    const progress = await progressPromise;
    
    // Ensure we have a valid report and activities
    if (!progress || !progress[0]) {
      throw new Error('Failed to generate progress report');
    }
    
    const [report, activities] = progress;

    const armScorePromise = additionalProps.skipARM ? Promise.resolve(null) : fetchARMScore(student, activities);
    const pendingAssessmentPromise = hasPendingAssessment(student.value);

    const [armScore, isPending] = await Promise.all([armScorePromise, pendingAssessmentPromise]);

    report.armScore = armScore;
    report.isPending = isPending;

    return [report, activities];
  } catch (error) {
    console.error("An error occurred while fetching the report:", error);
    throw error;
  }
};

const fetchProgressActivities = async (student, equating, locale, metric, scale, additionalProps) => {
  try {
    const activities = await fetchActivities(student, equating, locale, scale);
    if(!activities.student || !activities.student.grade){
      activities.map((act,index)=>activities[index].student=student);
    }

    // Get scaled cutlines from Redux state
    let scaledCutlines = null;
    const state = store.getState();
    const districtId = get(state, 'reports.roster.data.district[0].id');
    const schoolId = get(state, 'reports.roster.data.school[0].id');
    
    if (districtId && schoolId && student?.grade !== undefined) {
      try {
        // Wait for assessment calendar periods to be available
        await waitForState(state => {
          const calendar = state.calendar.assessmentCalendar;
          return calendar?.periods?.length > 0;
        });
        const assessmentCalendar = store.getState().calendar.assessmentCalendar;
        const periodIds = assessmentCalendar.periods.map(period => period.periodIdentifier);
        const params = {
          districtId,
          schoolId,
          grade: student.grade,
          scale: state.userEntitlement.data.scoreType || "ARM",
          periodId: periodIds
        };

        scaledCutlines = await fetchScaledCutlines(params);
        store.dispatch({
          type: 'PROGRESS_CUTLINES_COMPLETE',
          cutlines: scaledCutlines
        });
      } catch (error) {
        console.warn("Failed to get assessment calendar periods or fetch scaled cutlines:", error);
        return [null, activities];
      }
    } else {
      console.warn("Missing required data for cutlines:", { 
        hasDistrictId: !!districtId, 
        hasSchoolId: !!schoolId, 
        hasStudentGrade: student?.grade !== undefined 
      });
    }

    // Generate the report with the available cutlines
    let report = generateProgressReport(activities, metric, scale, locale, scaledCutlines, additionalProps.pageType);
    if (!report) {
      throw new Error('Failed to generate progress report');
    }

    report = accountForTrendline(report, scale, student, locale);
    return [report, activities];
  } catch (error) {
    console.error("Error in fetchProgressActivities:", error);
    throw error;
  }
};

export default {
  fetchActivities,
  fetchReport,
}